(() => {
  'use strict';
  if (window.nosteaKitCbOfferLoaded) return;
  window.nosteaKitCbOfferLoaded = true;

  const apiRoot = () => window.Shopify?.routes?.root || '/';
  async function requestJSON(path, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(apiRoot() + path, {
        method: body === undefined ? 'GET' : 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: controller.signal
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) {
        throw new Error(typeof result.description === 'string' ? result.description : 'Le panier n’a pas confirmé cette opération.');
      }
      return result;
    } finally { clearTimeout(timer); }
  }

  function giftIsDiscounted(cart, giftId, code) {
    return (cart.items || []).some(item => {
      if (giftId && String(item.variant_id || item.id) !== giftId) return false;
      const allocations = item.line_level_discount_allocations || [];
      const amount = allocations.filter(a => String(a.discount_application?.title || '').toUpperCase() === code)
        .reduce((sum, a) => sum + Number(a.amount || 0), 0);
      const unitPrice = Number(item.original_price || item.price || 0);
      return unitPrice > 0 && amount >= unitPrice;
    });
  }

  function init(root) {
    if (root.dataset.ready) return;
    const section = document.getElementById('shopify-section-' + root.dataset.sectionId);
    const form = document.getElementById(root.dataset.formId);
    const check = root.querySelector('[data-gift-check]');
    const choice = root.querySelector('[data-gift-variant]');
    if (!section || !form || !choice?.options.length || !check) return;
    const options = root.querySelector('[data-gift-options]');
    const status = root.querySelector('[data-gift-status]');
    const message = root.querySelector('[data-gift-message]');
    const cartLink = root.querySelector('[data-gift-cart-link]');
    let state = 'idle';

    function showStatus(text, cart = false) {
      message.textContent = text;
      status.hidden = false;
      cartLink.hidden = !cart;
      if (cart) status.focus({ preventScroll: false });
    }
    function refreshChoice() {
      options.hidden = !check.checked;
      choice.disabled = !check.checked || state !== 'idle';
      check.setAttribute('aria-expanded', String(check.checked));
    }
    check.addEventListener('change', refreshChoice);
    section.addEventListener('submit', async event => {
      // Only this product's main form, never recommendations or quick-buy forms.
      if (!root.isConnected || event.target.getAttribute('id') !== root.dataset.formId) return;
      if (state === 'idle' && !check.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (state !== 'idle') {
        if (state !== 'busy') showStatus('Une tentative d’ajout a déjà été effectuée. Vérifiez votre panier avant toute nouvelle commande.', true);
        return;
      }
      const activeForm = event.target;
      const button = activeForm.querySelector('[type="submit"]');
      if (button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;
      const data = new FormData(activeForm);
      const variantId = String(data.get('id') || '');
      const giftId = choice.value;
      const quantity = Number(data.get('quantity') || 1);
      if (!/^\d+$/.test(variantId) || !/^\d+$/.test(giftId) || !Number.isInteger(quantity) || quantity < 1) {
        showStatus('Choisissez une combinaison de parfums disponible avant d’ajouter votre kit.');
        return;
      }
      // Preserve any native line properties and selling plan without modifying the form.
      const kit = { id: Number(variantId), quantity };
      const properties = {};
      data.forEach((value, name) => {
        const match = /^properties\[(.+)\]$/.exec(name);
        if (match && typeof value === 'string') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) kit.properties = properties;
      if (data.get('selling_plan')) kit.selling_plan = data.get('selling_plan');
      state = 'busy';
      check.disabled = true;
      refreshChoice();
      button?.setAttribute('aria-disabled', 'true');
      button?.setAttribute('aria-busy', 'true');
      showStatus('Vérification de votre offre…');
      let attemptedAdd = false;
      let added = false;
      try {
        const code = root.dataset.code.toUpperCase();
        const before = await requestJSON('cart.js');
        if (giftIsDiscounted(before, null, code)) {
          state = 'idle';
          check.checked = false;
          check.disabled = false;
          refreshChoice();
          showStatus('Un pot offert est déjà dans votre panier. Vous pouvez ajouter le kit seul.', true);
          return;
        }
        showStatus('Ajout de votre kit et vérification du pot offert…');
        // Exactly one add request. Never retry automatically: a lost response may hide a successful add.
        attemptedAdd = true;
        await requestJSON('cart/add.js', { items: [kit, { id: Number(giftId), quantity: 1 }] });
        added = true;
        let cart = await requestJSON('cart/update.js', { discount: code });
        if (!giftIsDiscounted(cart, giftId, code)) cart = await requestJSON('cart.js');
        if (!giftIsDiscounted(cart, giftId, code)) {
          state = 'review';
          showStatus('Votre kit et le pot supplémentaire sont dans le panier, mais la gratuité du pot n’a pas pu être confirmée. Vérifiez la remise avant de payer ; le pot peut être facturé.', true);
          return;
        }
        state = 'added';
        showStatus('Votre pot offert est confirmé. Ouverture du panier…');
        window.location.assign(apiRoot() + 'cart');
      } catch (error) {
        state = attemptedAdd ? 'review' : 'idle';
        if (!attemptedAdd) {
          check.disabled = false;
          refreshChoice();
        }
        const detail = error.name === 'AbortError' || error instanceof TypeError
          ? 'La connexion a été interrompue.'
          : (error.message || 'La réponse du panier est indisponible.');
        if (!attemptedAdd) {
          showStatus('Votre panier n’a pas pu être vérifié. Aucun ajout n’a été demandé. Vous pouvez réessayer.');
          return;
        }
        showStatus(added
          ? 'Votre kit et le pot ont été ajoutés, mais la remise n’a pas pu être confirmée. ' + detail + ' Vérifiez votre panier avant de payer.'
          : detail + ' L’ajout n’est pas confirmé. Vérifiez votre panier avant de réessayer pour éviter un doublon.', true);
      } finally {
        button?.removeAttribute('aria-busy');
        // Keep the offer/form guarded after any add attempt; existing cart contents are never removed.
        button?.removeAttribute('aria-disabled');
      }
    }, true);
    root.dataset.ready = 'true';
    refreshChoice();
    root.hidden = false;
  }
  function initPreviewRoute(scope) {
    const pageURL = new URL(window.location.href);
    if (pageURL.searchParams.get('view') !== 'nostea-kit-cb') return;
    scope.querySelectorAll('.nostea-kit-cb__subtitle').forEach(intro => {
      const section = intro.closest('[id^="shopify-section-"]');
      section?.querySelectorAll('variant-options').forEach(picker => {
        if (picker.dataset.nosteaCbPreviewReady) return;
        const productURL = new URL(picker.dataset.url, window.location.origin);
        productURL.searchParams.set('view', 'nostea-kit-cb');
        if (pageURL.searchParams.has('preview_theme_id')) {
          productURL.searchParams.set('preview_theme_id', pageURL.searchParams.get('preview_theme_id'));
        }
        // The native section request uses new URL(data-url). Preserve its alternate template.
        picker.dataset.url = productURL.pathname + productURL.search;
        picker.dataset.updateUrl = 'false';
        picker.addEventListener('variantChangeEnd', event => {
          if (event.target !== picker || !picker.currentVariant?.id) return;
          const nextURL = new URL(window.location.href);
          nextURL.searchParams.set('view', 'nostea-kit-cb');
          if (pageURL.searchParams.has('preview_theme_id')) {
            nextURL.searchParams.set('preview_theme_id', pageURL.searchParams.get('preview_theme_id'));
          }
          nextURL.searchParams.set('variant', picker.currentVariant.id);
          window.history.replaceState(window.history.state, '', nextURL);
        });
        picker.dataset.nosteaCbPreviewReady = 'true';
      });
    });
  }
  function scan(scope) {
    initPreviewRoute(scope);
    scope.querySelectorAll('[data-nostea-kit-offer]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
